import { apiClient } from '@indipia/shared';
import type { ApiResponse } from '@indipia/shared-types';
import { QUERY_TIMEOUT_MS } from './orderApi';
import {
  shipParseArrivalNoticeResult,
  shipParseLedger,
  shipParseList,
  shipParseOrderScheduleResult,
  shipParsePhotos,
  shipParseReceiptResult,
  shipParseRequestResult,
  ShipParseError,
} from '../constants/ship.logic';
import type {
  ShipArrivalNoticeResult,
  ShipDeliveryList,
  ShipLedgerRow,
  ShipOrderScheduleBody,
  ShipOrderScheduleResult,
  ShipPhoto,
  ShipReceiptBody,
  ShipReceiptResult,
  ShipRequestResult,
} from '../constants/ship.logic';

// WP5 6단계 — 담당자 화면 호출(지시서 §3.1·§7.1). 경로는 apiClient baseURL(/api) 기준이라 '/schedule/...' 로 쓴다.
// 요청 헤더(X-Comp-Id·X-User-Id)는 게이트웨이(FlowGate)가 주입하므로 여기서 싣지 않는다(로컬 개발 우회는 shared client.ts 인터셉터가 처리). 응답은 ship.logic 파서로 검증하고
// 형태가 약속 밖이면 ShipParseError 로 던진다(폴백 없음). 쓰기는 자동 재시도 없음.

function unwrap(res: { data: ApiResponse<unknown> }): unknown {
  const d = res.data?.data;
  if (d === undefined || d === null) throw new ShipParseError('배송정보 응답에 data 가 없음');
  return d;
}

/** C7 — 배송정보현황 목록 */
export async function getShipDeliveries(scheduleId: number, signal?: AbortSignal): Promise<ShipDeliveryList> {
  const res = await apiClient.get<ApiResponse<unknown>>(`/schedule/schedules/${scheduleId}/order-deliveries`, { signal, timeout: QUERY_TIMEOUT_MS });
  return shipParseList(unwrap(res));
}

/** C1 — 지금 요청·다시 요청 (본문 없음) */
export async function requestShip(orderVendorId: number): Promise<ShipRequestResult> {
  const res = await apiClient.post<ApiResponse<unknown>>(`/schedule/order-vendors/${orderVendorId}/ship-requests`, undefined);
  return shipParseRequestResult(unwrap(res));
}

/** C5 — 반장 도착 안내. force 를 주면 본문 `{force}`, 생략하면 본문 없음. */
export async function sendShipArrivalNotice(orderVendorId: number, force?: boolean): Promise<ShipArrivalNoticeResult> {
  const res = await apiClient.post<ApiResponse<unknown>>(
    `/schedule/order-vendors/${orderVendorId}/arrival-notices`,
    force === undefined ? undefined : { force },
  );
  return shipParseArrivalNoticeResult(unwrap(res));
}

/** C4a — 사진 목록(종류·순번 순, 서버 순서 그대로) */
export async function getShipPhotos(orderVendorId: number): Promise<ShipPhoto[]> {
  const res = await apiClient.get<ApiResponse<unknown>>(`/schedule/order-vendors/${orderVendorId}/delivery-photos`, { timeout: QUERY_TIMEOUT_MS });
  return shipParsePhotos(unwrap(res));
}

/** C4b — 사진 바이트(상차·하차 같은 경로, 하차 중계는 서버가 처리). 회신 Blob. */
export async function fetchShipPhotoBlob(
  orderVendorId: number,
  orderPhotoId: number,
  size: 'thumb' | 'original',
  signal?: AbortSignal,
): Promise<Blob> {
  const res = await apiClient.get<Blob>(`/schedule/order-vendors/${orderVendorId}/delivery-photos/${orderPhotoId}`, {
    params: { size },
    responseType: 'blob',
    signal,
  });
  return res.data;
}

/** A1 — 간트·팝업용 원장 행(활성 목록) */
export async function getShipLedger(scheduleId: number): Promise<ShipLedgerRow[]> {
  const res = await apiClient.get<ApiResponse<unknown>>(`/schedule/schedules/${scheduleId}/order-vendors`, { timeout: QUERY_TIMEOUT_MS });
  return shipParseLedger(unwrap(res));
}

/** C6 — 담당자 직접 입고 확인(사진 없음). 배송정보현황 행의 [직접 입고 확인] 창에서 호출 */
export async function confirmShipReceipt(orderVendorId: number, body: ShipReceiptBody): Promise<ShipReceiptResult> {
  const res = await apiClient.post<ApiResponse<unknown>>(`/schedule/order-vendors/${orderVendorId}/receipts`, body);
  return shipParseReceiptResult(unwrap(res));
}

/** C9 — 발주일·입고희망일 변경(바뀐 필드 한 개만 보냄. 없는 필드=변경 없음, null=비움) */
export async function patchShipOrderSchedule(taskId: number, body: ShipOrderScheduleBody): Promise<ShipOrderScheduleResult> {
  const res = await apiClient.patch<ApiResponse<unknown>>(`/schedule/tasks/${taskId}/order-schedule`, body);
  return shipParseOrderScheduleResult(unwrap(res));
}
